// src/common/LocalSeoCommonComponents/BusinessProfileSelector.tsx
import {
  Avatar,
  Box,
  Button,
  List,
  ListItem,
  Popover,
  Tooltip,
  Typography
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import { useRef, useState } from 'react';
import { LocalSeoAllSx } from 'src/content/localSeo/localSeoCss';
import { siteAuditStyles } from 'src/layouts/SidebarLayout/Sidebar/SidebarMenu/sideBarCss';
import {
  SqSpIconPath,
  SqSpIconTooltipLocalSEOText
} from 'src/common/config/TextMessage';
import { ListDataTableCss } from '../keywordResearch/keywordsLists/KeywordListsCss';
import { newCardsCss } from '../dashboard/DashboardCss';

interface LocationItem {
  name: string;
  title: string;
  isConnectedWithSqsp: boolean;
  isDelete?: boolean;
}

interface BusinessProfileSelectorProps {
  selectedAddress: LocationItem | null;
  locations: LocationItem[];
  isEnterprise?: boolean;
  enterpriseLimit?: number;
  onSelect: (location: LocationItem) => void;
  onAddProfile: () => void;
  onViewAll?: () => void;
  isExtraSx?: boolean;
  isPdfBuilder?: boolean;
}

export const BusinessProfileSelector = ({
  selectedAddress,
  locations,
  isEnterprise = false,
  enterpriseLimit = 3,
  onSelect,
  onAddProfile,
  onViewAll,
  isExtraSx = null,
  isPdfBuilder = false
}: BusinessProfileSelectorProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const businessRef = useRef<HTMLDivElement | null>(null);

  const open = Boolean(anchorEl);

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(open ? null : event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      {/* Selector Button */}
      <Box
        ref={businessRef}
        onClick={handleToggle}
        sx={{
          ...LocalSeoAllSx?.businessFilterBox,
          ...(isExtraSx ? LocalSeoAllSx?.extraFilterBoxSx : {})
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            component="img"
            src="/static/images/icons/local-seo/google-home.svg"
            alt="GMB"
            sx={{ width: 20, height: 20 }}
          />
          <Typography
            sx={{
              ...LocalSeoAllSx?.businessTitle,
              opacity: selectedAddress?.isDelete ? 0.5 : 1
            }}
          >
            {selectedAddress?.title || 'Select Business'}
          </Typography>
          {selectedAddress?.isConnectedWithSqsp && (
            <Tooltip
              title={
                <div className="flex items-center">
                  <p
                    style={{
                      color: 'white',
                      fontSize: '12px'
                    }}
                  >
                    {SqSpIconTooltipLocalSEOText}
                  </p>
                </div>
              }
              placement="top"
            >
              <Avatar sx={newCardsCss?.avatarSx} src={SqSpIconPath} />
            </Tooltip>
          )}
        </Box>
        {open ? (
          <ExpandLessIcon fontSize="small" sx={{ color: 'var(--color-primary)' }} />
        ) : (
          <ExpandMoreIcon fontSize="small" sx={{ color: 'var(--color-primary)' }} />
        )}
      </Box>

      {/* Dropdown Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            minWidth: businessRef.current?.clientWidth || 240,
            borderRadius: '6px',
            paddingTop: '8px',
            paddingBottom: '8px',
            border: '1px solid var(--color-border-muted)',
            boxShadow: '0px 4px 6px -2px #10182808'
          }
        }}
        disableScrollLock={true}
      >
        <List sx={{ maxHeight: '200px', overflow: 'auto' }}>
          {locations?.map((location) => (
            <ListItem
              key={location?.name}
              button
              onClick={() => {
                onSelect(location);
                handleClose();
              }}
              sx={{ padding: '8px 16px' }}
            >
              <Box sx={LocalSeoAllSx?.businessListItemBox}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    sx={{
                      ...(selectedAddress?.name === location.name
                        ? LocalSeoAllSx?.selectedBusinessNameText
                        : LocalSeoAllSx?.nonSelectedBusinessNameText),
                      opacity: location?.isDelete ? 0.5 : 1
                    }}
                  >
                    {location?.title}
                  </Typography>
                  {location?.isConnectedWithSqsp && (
                    <Tooltip
                      title={
                        <div className="flex items-center">
                          <p
                            style={{
                              color: 'white',
                              fontSize: '12px'
                            }}
                          >
                            {SqSpIconTooltipLocalSEOText}
                          </p>
                        </div>
                      }
                      placement="top"
                    >
                      <Avatar sx={newCardsCss?.avatarSx} src={SqSpIconPath} />
                    </Tooltip>
                  )}
                </Box>
                {selectedAddress?.name === location.name && (
                  <CheckIcon fontSize="small" color="primary" />
                )}
              </Box>
            </ListItem>
          ))}
        </List>

        {/* Footer Actions */}
        <Box sx={{ padding: '0px 8px' }}>
          {isEnterprise && !isPdfBuilder ? (
            <Box sx={siteAuditStyles?.viewAllSiteBtnBox}>
              <Button
                fullWidth
                variant="contained"
                onClick={onAddProfile}
                startIcon={<AddIcon sx={ListDataTableCss?.addIcon} />}
                sx={siteAuditStyles?.auditNewSiteBtn}
              >
                Add Profile
              </Button>
              {onViewAll && (
                <Button
                  fullWidth
                  variant="outlined"
                  sx={siteAuditStyles?.viewAllSitesBtn}
                  onClick={onViewAll}
                >
                  {`View All Profiles (${locations?.length || 0})`}
                </Button>
              )}
            </Box>
          ) : !isPdfBuilder ? (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon fontSize="small" sx={{ color: 'var(--color-primary)' }} />}
              sx={LocalSeoAllSx?.addProfileBtn}
              onClick={onAddProfile}
            >
              <Typography sx={LocalSeoAllSx?.addProfileText}>
                Add Profile
              </Typography>
            </Button>
          ) : (
            <></>
          )}
        </Box>
      </Popover>
    </>
  );
};
